import { useReadContracts, useWatchContractEvent, usePublicClient } from 'wagmi';
import { Address, PublicClient, Log } from 'viem';
import { useMemo, useState, useEffect } from 'react';
import { GN } from '../data/GoodNumber';
import { useAccount } from 'wagmi';

const BLOCK_RANGE = 9000n; // Slightly less than 10,000 to be safe

const CONTRACT_ADDRESSES = [
  '0x954d1AD7db7AF19Fa9ba88E3b1a3012D458cE108',
  '0x9c06739dBB6118551cCCeb00553fEAf3568ca107'
] as const;

const REWARDS_ABI = [{
  type: 'function',
  name: 'rewardsOf',
  stateMutability: 'view',
  inputs: [
    {
      internalType: 'address',
      name: 'account',
      type: 'address'
    }
  ],
  outputs: [
    {
      internalType: 'uint96',
      name: '',
      type: 'uint96'
    }
  ]
}] as const;

const DEPOSIT_EVENT = {
  type: 'event',
  name: 'Deposit',
  inputs: [
    {
      indexed: true,
      name: "caller",
      type: "address"
    },
    {
      indexed: true,
      name: "owner",
      type: "address"
    },
    {
      indexed: false,
      name: "assets",
      type: "uint256"
    },
    {
      indexed: false,
      name: "shares",
      type: "uint256"
    }
  ]
} as const;

export interface LeaderboardEnsEntry {
  name: string | null;
  lastChecked: number;
}

export interface LeaderboardEntry {
  address: string;
  score: GN;
  ens?: LeaderboardEnsEntry;
}

function safeCreateGN(value: bigint | number | string | null | undefined): GN {
  if (value === null || value === undefined) return GN.zero(18);
  const valueString = value.toString();
  try {
    return new GN(valueString, 18, 10);
  } catch (error) {
    console.error('Error creating GN:', error, 'Value:', valueString);
    return GN.zero(18);
  }
}

async function getLogsInChunks(
  client: PublicClient,
  contractAddress: Address,
  fromBlock: bigint,
  toBlock: bigint
) {
  const allLogs = [];
  let currentFromBlock = fromBlock;

  while (currentFromBlock <= toBlock) {
    const currentToBlock = (currentFromBlock + BLOCK_RANGE) > toBlock 
      ? toBlock 
      : currentFromBlock + BLOCK_RANGE;

    try {
      console.log(`Fetching logs for range ${currentFromBlock} to ${currentToBlock}`);
      const logs = await client.getLogs({
        address: contractAddress,
        event: {
          anonymous: false,
          inputs: [
            {
              indexed: true,
              name: "caller",
              type: "address"
            },
            {
              indexed: true,
              name: "owner",
              type: "address"
            },
            {
              indexed: false,
              name: "assets",
              type: "uint256"
            },
            {
              indexed: false,
              name: "shares",
              type: "uint256"
            }
          ],
          name: "Deposit",
          type: "event"
        },
        fromBlock: currentFromBlock,
        toBlock: currentToBlock,
      });
      
      console.log(`Found ${logs.length} logs in this chunk`, logs);
      allLogs.push(...logs);
    } catch (error) {
      console.error('Error fetching logs for range:', { currentFromBlock, currentToBlock }, error);
    }

    currentFromBlock = currentToBlock + 1n;
  }

  return allLogs;
}

function processLog(log: Log): Address[] {
  const addresses: Address[] = [];
  
  console.log('Processing log with topics:', log.topics);
  
  // Topic[0] is the event signature
  // Topic[1] is the caller (first indexed parameter)
  // Topic[2] is the owner (second indexed parameter)
  if (log.topics?.[2] && log.topics?.[1]) {
    // Get owner address from topic[2]
    const ownerTopic = log.topics[2];
    if (typeof ownerTopic === 'string') {
      const ownerAddress = `0x${ownerTopic.slice(-40)}` as Address;
      console.log('Extracted owner address:', ownerAddress);
      addresses.push(ownerAddress);
    }

    // Get caller address from topic[1]
    const callerTopic = log.topics[1];
    if (typeof callerTopic === 'string') {
      const callerAddress = `0x${callerTopic.slice(-40)}` as Address;
      console.log('Extracted caller address:', callerAddress);
      addresses.push(callerAddress);
    }
  } else {
    console.log('Log has insufficient topics:', log.topics.length);
  }

  return addresses;
}

function useUniqueAddresses() {
  const [addresses, setAddresses] = useState<Set<Address>>(new Set());
  const client = usePublicClient();

  // Fetch historical events
  useEffect(() => {
    async function fetchPastEvents(publicClient: NonNullable<PublicClient>) {
      try {
        const fromBlock = BigInt(10_000_000);
        const toBlock = await publicClient.getBlockNumber();
        
        const allAddresses = new Set<Address>();

        for (const contractAddress of CONTRACT_ADDRESSES) {
          console.log('Fetching events for contract:', contractAddress);
          const logs = await getLogsInChunks(publicClient, contractAddress, fromBlock, toBlock);
          console.log(`Found ${logs.length} total Deposit events for ${contractAddress}`);

          logs.forEach((log, index) => {
            console.log(`Processing log ${index + 1}/${logs.length} from ${contractAddress}:`, log.topics);
            const foundAddresses = processLog(log);
            foundAddresses.forEach(addr => {
              console.log(`Adding address to set: ${addr}`);
              allAddresses.add(addr);
            });
          });
          
          console.log(`Current unique addresses after processing ${contractAddress}:`, Array.from(allAddresses));
        }

        console.log(`Final set of unique addresses:`, Array.from(allAddresses));
        setAddresses(allAddresses);
      } catch (error) {
        console.error('Error fetching past events:', error);
      }
    }

    if (!client) return;
    fetchPastEvents(client);
  }, [client]);

  // Watch for new events on both contracts
  useWatchContractEvent({
    address: CONTRACT_ADDRESSES[0],
    abi: [DEPOSIT_EVENT],
    eventName: 'Deposit',
    onLogs(logs) {
      setAddresses(prev => {
        const newSet = new Set(prev);
        logs.forEach(log => {
          const foundAddresses = processLog(log);
          foundAddresses.forEach(addr => newSet.add(addr));
        });
        return newSet;
      });
    },
  });

  useWatchContractEvent({
    address: CONTRACT_ADDRESSES[1],
    abi: [DEPOSIT_EVENT],
    eventName: 'Deposit',
    onLogs(logs) {
      setAddresses(prev => {
        const newSet = new Set(prev);
        logs.forEach(log => {
          const foundAddresses = processLog(log);
          foundAddresses.forEach(addr => newSet.add(addr));
        });
        return newSet;
      });
    },
  });

  return Array.from(addresses);
}

export function useLeaderboard(refetchInterval = 10 * 60 * 1000) {
  const uniqueAddresses = useUniqueAddresses();
  const { address: connectedAddress } = useAccount();
  
  console.log('Current unique addresses:', uniqueAddresses.length);
  
  // Always include the connected address if it exists
  const addressesToQuery = useMemo(() => {
    const addresses = new Set(uniqueAddresses);
    if (connectedAddress) {
      addresses.add(connectedAddress);
    }
    const addressArray = Array.from(addresses);
    console.log('Addresses to query:', addressArray.length);
    return addressArray;
  }, [uniqueAddresses, connectedAddress]);

  const { data: rewardsData, error, status } = useReadContracts({
    contracts: CONTRACT_ADDRESSES.flatMap(contractAddress => 
      addressesToQuery.map(userAddress => ({
        address: contractAddress as Address,
        abi: REWARDS_ABI,
        functionName: 'rewardsOf',
        args: [userAddress],
      }))
    ),
    query: {
      enabled: addressesToQuery.length > 0,
      refetchInterval,
    }
  });

  return useMemo(() => {
    if (error || status !== 'success' || !Boolean(rewardsData)) {
      console.log('Error or no data:', { error, status, hasData: Boolean(rewardsData) });
      return { data: [] };
    }

    // Group rewards by user address
    const userScores = new Map<string, GN>();
    
    console.log('Processing rewards for addresses:', addressesToQuery);
    console.log('Raw rewards data:', rewardsData);
    
    addressesToQuery.forEach((address, userIndex) => {
      const totalScore = CONTRACT_ADDRESSES.reduce((sum, contractAddress, contractIndex) => {
        const result = rewardsData[userIndex * CONTRACT_ADDRESSES.length + contractIndex]?.result;
        const score = safeCreateGN(result);
        console.log(`Score for address ${address} from contract ${contractAddress}:`, score.toString());
        return sum.add(score);
      }, GN.zero(18));
      
      console.log(`Total score for address ${address}:`, totalScore.toString());
      userScores.set(address.toLowerCase(), totalScore);
    });

    // Convert to array and sort by score
    const entries: LeaderboardEntry[] = Array.from(userScores.entries())
      .map(([address, score]) => {
        console.log(`Pre-filter entry - Address: ${address}, Score: ${score.toString()}`);
        return {
          address,
          score,
        };
      })
      .filter(entry => {
        const keep = !entry.score.isZero();
        console.log(`Filtering entry - Address: ${entry.address}, Score: ${entry.score.toString()}, Keep: ${keep}`);
        return keep;
      })
      .sort((a, b) => Number(b.score.toString()) - Number(a.score.toString()));

    console.log('Final entries:', entries);
    return { data: entries };
  }, [rewardsData, addressesToQuery, error, status]);
}

export function useLeaderboardValue(address?: Address) {
  const { data: rewardsData, error, status } = useReadContracts({
    contracts: CONTRACT_ADDRESSES.map(contractAddress => ({
      address: contractAddress as Address,
      abi: REWARDS_ABI,
      functionName: 'rewardsOf',
      args: address ? [address] : undefined,
    })),
    query: {
      enabled: Boolean(address),
      refetchInterval: 10_000,
    }
  });

  return useMemo(() => {
    if (!address || error || status !== 'success' || !Boolean(rewardsData)) {
      return GN.zero(18);
    }

    return rewardsData.reduce((sum, result) => {
      return sum.add(safeCreateGN(result?.result));
    }, GN.zero(18));
  }, [rewardsData, address, error, status]);
}

export type LeaderboardPageEntry = LeaderboardEntry;